import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useToast } from '../../context/ToastContext';
import { registerCompany } from '../../api/company';

const step1Schema = z.object({
  name: z.string().min(2, 'Company name is required'),
  email: z.string().email('Invalid work email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  website: z.string().url('Must be a valid URL (include http:// or https://)'),
  industry: z.string().min(1, 'Industry is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const step2Schema = z.object({
  description: z.string().min(10, 'Provide a brief description'),
  location: z.string().min(2, 'Location is required'),
  hr_name: z.string().min(2, 'HR Contact Name is required'),
  hr_contact: z.string().min(10, 'Valid phone number required'),
});

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Education', 
  'Manufacturing', 'Retail', 'Consulting', 'Other'
];

const CompanyRegister = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const { register: registerStep1, handleSubmit: handleStep1, formState: { errors: errors1 }, watch } = useForm({
    resolver: zodResolver(step1Schema)
  });
  
  const { register: registerStep2, handleSubmit: handleStep2, formState: { errors: errors2, isSubmitting } } = useForm({
    resolver: zodResolver(step2Schema)
  });

  const onStep1Submit = (data) => {
    setFormData({ ...data });
    setStep(2);
  };

  const onStep2Submit = async (data) => {
    const finalData = { ...formData, ...data };
    try {
      const res = await registerCompany(finalData);
      addToast('Company registered successfully! Please verify your email.', 'success');
      navigate(`/verify-company-email/${res?.id || 'new'}`, { state: { email: finalData.email } });
    } catch (err) {
      addToast(err.response?.data?.detail || 'Registration failed. Email might already exist.', 'error');
    }
  };

  return (
    <div className="auth-page" style={{ background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-200) 100%)' }}>
      <div className="auth-card auth-card-wide border-t-4 border-t-gray-900">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon bg-gray-900 text-white">M</div>
          <div>Million<span>Minds</span> <span className="text-sm font-normal text-gray-500 ml-2">For Employers</span></div>
        </Link>
        
        <div className="text-center mb-6">
          <h1 className="auth-title">Register Your Company</h1>
          <p className="auth-subtitle">Join our platform to hire the best talent from top colleges.</p>
        </div>
        
        <div className="flex items-center justify-between mb-8 relative">
          <div className="absolute left-0 top-1/2 w-full h-1 bg-gray-200 -z-10 transform -translate-y-1/2"></div>
          <div className="absolute left-0 top-1/2 h-1 bg-gray-900 -z-10 transform -translate-y-1/2 transition-all duration-300" style={{ width: step === 1 ? '50%' : '100%' }}></div>
          
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1(onStep1Submit)}>
            <div className="form-row">
              <Input
                label="Company Name"
                placeholder="Acme Corp"
                {...registerStep1('name')}
                error={errors1.name?.message}
              />
              <Input
                label="Work Email"
                type="email"
                placeholder="hr@acmecorp.com"
                {...registerStep1('email')}
                error={errors1.email?.message}
              />
            </div>
            
            <div className="form-row">
              <Input
                label="Website"
                placeholder="https://acmecorp.com"
                {...registerStep1('website')}
                error={errors1.website?.message}
              />
              <div className="form-group">
                <label className="form-label">Industry</label>
                <select className="form-input select" {...registerStep1('industry')}>
                  <option value="">Select Industry</option>
                  {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                </select>
                {errors1.industry && <span className="form-error">{errors1.industry.message}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                {...registerStep1('password')}
                error={errors1.password?.message}
              />
              <Input
                label="Confirm Password"
                type="password"
                placeholder="••••••••"
                {...registerStep1('confirmPassword')}
                error={errors1.confirmPassword?.message}
              />
            </div>
            
            <div className="form-actions mt-8">
              <Button type="submit" variant="secondary" className="w-full bg-gray-900 text-white hover:bg-black" size="lg">
                Continue to Profile
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleStep2(onStep2Submit)}>
            <div className="form-group">
              <label className="form-label">Company Description</label>
              <textarea 
                className={`form-input textarea ${errors2.description ? 'error' : ''}`}
                placeholder="Tell candidates about your company..."
                {...registerStep2('description')}
              ></textarea>
              {errors2.description && <span className="form-error">{errors2.description.message}</span>}
            </div>
            
            <Input
              label="Headquarters Location"
              placeholder="City, State, Country"
              {...registerStep2('location')}
              error={errors2.location?.message}
            />
            
            <div className="form-row mt-4">
              <Input
                label="Primary HR Contact Name"
                placeholder="Jane Doe"
                {...registerStep2('hr_name')}
                error={errors2.hr_name?.message}
              />
              <Input
                label="HR Contact Phone"
                placeholder="+1 234 567 8900"
                {...registerStep2('hr_contact')}
                error={errors2.hr_contact?.message}
              />
            </div>
            
            <div className="form-actions mt-8">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button type="submit" variant="secondary" className="flex-1 bg-gray-900 text-white hover:bg-black" size="lg" isLoading={isSubmitting}>
                Complete Registration
              </Button>
            </div>
          </form>
        )}
        
        <div className="auth-footer mt-6">
          Already registered? <Link to="/company/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegister;